import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import TextInput from "../../../components/inputs/TextInput";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import { useTranslation } from "react-i18next";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import {
  allowedFileTypes,
  ContentType,
  Vehicle,
  WAREHOUSE_IN_OUT_PERMIT_ATTACHMENTS_TYPE,
  WarehousePermitWorker,
} from "../../../constants/common";
import { v4 as uuidv4 } from "uuid";
import { Button } from "../../../components/button";
import { createPermitVehicleSchema } from "../../../schemas/permitVehicleSchema";
import FileUploadInput from "../../../components/inputs/FileUploadInput";
import toast from "react-hot-toast";
import ViewAttachmentModal from "../../../components/modal/ViewAttachmentModal";
import { useGetWarehouseInOutPermitFileByType } from "../../../hooks/permit/mutation";

interface VehicleFormProps {
  setAddedVehicles: Dispatch<SetStateAction<Vehicle[]>>;
  setIsAddVehicleFormVisible: Dispatch<SetStateAction<boolean>>;
  editingVehicle: Vehicle | null;
  setEditingVehicle: Dispatch<SetStateAction<Vehicle | null>>;
  plateSource:
    | { labelEn: string; labelAr: string; value: number }[]
    | undefined;
  plateLetters: {
    labelEn: any;
    labelAr: any;
    value: any;
  }[];
  driversNameOptions: {
    labelEn: string;
    labelAr: string;
    value: string;
  }[];
  addedWorkers: WarehousePermitWorker[];
  isFieldsDisabled: boolean;
}

const VehicleForm = ({
  setAddedVehicles,
  setIsAddVehicleFormVisible,
  editingVehicle,
  setEditingVehicle,
  plateSource,
  plateLetters,
  driversNameOptions,
  addedWorkers,
  isFieldsDisabled,
}: VehicleFormProps) => {
  const { t } = useTranslation();

  const [isAttachments, setIsAttachments] = useState({
    haveVehicleRegLicense: 0,
  });

  const uploadVehicleRegLicenseRef = useRef<HTMLInputElement | null>(null);
  const [vehicleRegLicenseFileName, setVehicleRegLicenseFileName] =
    useState("");

  const [previewFiles, setPreviewFiles] = useState<
    Record<number, { url: string; contentType: string }>
  >({});
  const [imageSrc, setImageSrc] = useState("");
  const [contentType, setContentType] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);
  const [selectedType, setSelectedType] = useState<number>(0);
  const [isGetFileByTypeLoading, setIsGetFileByTypeLoading] = useState(false);

  const {
    mutateAsync: GetWarehouseInOutPermitFileByType,
    isPending: isGetWarehouseInOutPermitFileByTypeLoading,
  } = useGetWarehouseInOutPermitFileByType();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
  } = useFormWithSchema(createPermitVehicleSchema(isAttachments));

  useEffect(() => {
    if (editingVehicle) {
      reset(editingVehicle);

      setVehicleRegLicenseFileName(
        editingVehicle?.uploadVehicleRegLicense?.name,
      );

      setIsAttachments({
        haveVehicleRegLicense: editingVehicle?.uploadVehicleRegLicense ? 1 : 0,
      });
    } else {
      reset();
    }
  }, [editingVehicle, reset]);

  const onSubmit = useCallback(
    async (data: any) => {
      const id = uuidv4();

      const plateType = plateSource?.find(
        (item) => item.value === Number(data.plateSource),
      );

      const plateTypeEn = plateType?.labelEn;
      const plateTypeAr = plateType?.labelAr;

      const commonDetails = {
        plateTypeEn,
        plateTypeAr,
      };

      const workerId = addedWorkers.find(
        (worker) => worker.name === data.driversName,
      )?.id;

      if (editingVehicle) {
        setAddedVehicles((prev) =>
          prev.map((visitor) =>
            visitor.id === editingVehicle.id
              ? {
                  id: editingVehicle.id,
                  ...data,
                  ...commonDetails,
                  driversId: workerId,
                }
              : visitor,
          ),
        );
        setEditingVehicle(null);
      } else {
        setAddedVehicles((prev) => [
          ...prev,
          {
            id,
            ...data,
            ...commonDetails,
            driversId: workerId,
          },
        ]);
      }

      reset();
      setIsAddVehicleFormVisible(false);
    },
    [
      editingVehicle,
      plateSource,
      reset,
      setAddedVehicles,
      setEditingVehicle,
      setIsAddVehicleFormVisible,
      addedWorkers,
    ],
  );

  const handleAttachmentView = useCallback(
    async (type: number) => {
      try {
        // If we already have a local preview for this attachment type
        if (previewFiles && previewFiles[type]) {
          setImageSrc(previewFiles[type].url);
          setContentType(previewFiles[type].contentType);
          setIsViewAttachmentModalOpen(true);
          return;
        }

        if (editingVehicle) {
          if (typeof editingVehicle?.uploadVehicleRegLicense !== "boolean") {
            const file = editingVehicle?.uploadVehicleRegLicense;

            if (typeof file === "object" && file instanceof File) {
              const url = URL.createObjectURL(file);
              setImageSrc(url);
              setContentType(file.type);
              setIsViewAttachmentModalOpen(true);
            }
            return;
          }
        }

        if (editingVehicle?.personnelId) {
          setSelectedType(type);

          const body = {
            Id: editingVehicle?.personnelId,
            Type: type,
          };

          const res = await GetWarehouseInOutPermitFileByType(body);

          setIsGetFileByTypeLoading(isGetWarehouseInOutPermitFileByTypeLoading);

          if (res) {
            let url = "";
            if (res?.contentType !== ContentType.PDF) {
              url = `data:${res?.contentType};base64,${res?.photoFile}`;
            } else {
              const binaryString = atob(res?.photoFile);
              const byteArray = new Uint8Array(binaryString.length);

              for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
              }

              const blob = new Blob([byteArray], { type: ContentType.PDF });
              url = URL.createObjectURL(blob);
            }
            setImageSrc(url);
            setContentType(res?.contentType);
            setIsViewAttachmentModalOpen(true);
          }
        }
      } catch (error) {
        toast.error("Something went wrong");
      } finally {
        setSelectedType(0);
      }
    },
    [
      GetWarehouseInOutPermitFileByType,
      editingVehicle,
      isGetWarehouseInOutPermitFileByTypeLoading,
      previewFiles,
    ],
  );

  return (
    <>
      <form className="flex flex-col gap-2 lg:gap-4">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
          <Controller
            name="plateSource"
            control={control}
            render={({ field: { name, onChange, value } }) => {
              return (
                <ComboboxInput
                  options={plateSource}
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  value={
                    plateSource?.find((item) => item.value === Number(value))
                      ?.labelEn || ""
                  }
                  error={errors?.[name]?.message}
                  label={t("plateType")}
                  required
                  readOnly={isFieldsDisabled}
                />
              );
            }}
          />

          <Controller
            name="plateLetter1"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <ComboboxInput
                options={plateLetters}
                value={
                  plateLetters?.find((item) => item.value === Number(value))
                    ?.labelEn || ""
                }
                onChange={(value) => {
                  onChange(value);
                  clearErrors(name);
                }}
                error={errors?.[name]?.message}
                label={t("plateLetter1")}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="plateLetter2"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <ComboboxInput
                options={plateLetters}
                value={
                  plateLetters?.find((item) => item.value === Number(value))
                    ?.labelEn || ""
                }
                onChange={(value) => {
                  onChange(value);
                  clearErrors(name);
                }}
                error={errors?.[name]?.message}
                label={t("plateLetter2")}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="plateLetter3"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <ComboboxInput
                options={plateLetters}
                value={
                  plateLetters?.find((item) => item.value === Number(value))
                    ?.labelEn || ""
                }
                onChange={(value) => {
                  onChange(value);
                  clearErrors(name);
                }}
                error={errors?.[name]?.message}
                label={t("plateLetter3")}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="plateNumber"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("plateNumber")}
                value={value || ""}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="color"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("color")}
                value={value || ""}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="driversName"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <ComboboxInput
                options={driversNameOptions}
                value={
                  driversNameOptions?.find((item) => item.value === value)
                    ?.labelEn || ""
                }
                onChange={(value) => {
                  onChange(value);
                  clearErrors(name);
                }}
                error={errors?.[name]?.message}
                label={t("driversName")}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <FileUploadInput
            name="uploadVehicleRegLicense"
            label={t("vehicleRegistrationLicense")}
            accept=".png,.jpg,.jpeg,.pdf"
            allowedTypes={allowedFileTypes}
            isAttachments={isAttachments?.haveVehicleRegLicense}
            control={control}
            uploadRef={uploadVehicleRegLicenseRef}
            disabled={isFieldsDisabled}
            fileName={vehicleRegLicenseFileName}
            setFileName={setVehicleRegLicenseFileName}
            handleAttachmentView={handleAttachmentView}
            selectedType={selectedType}
            attachmentType={
              WAREHOUSE_IN_OUT_PERMIT_ATTACHMENTS_TYPE.VEHICLE_REGISTRATION_LICENSE
            }
            isGetFileByTypeLoading={isGetFileByTypeLoading}
            error={errors.uploadVehicleRegLicense}
            // required={!isVIP}
            previewFiles={previewFiles}
            setPreviewFiles={setPreviewFiles}
            onFileUpload={(file) => {
              setIsAttachments((prev) => ({
                ...prev,
                haveVehicleRegLicense: file ? 1 : 0,
              }));
              // Clear validation error when file is uploaded
              if (file) {
                clearErrors("uploadVehicleRegLicense");
              }
            }}
            isRemovable={false}
          />
        </div>

        <div className="flex items-center justify-between">
          <div />
          <div className="flex items-center gap-2">
            {!isFieldsDisabled && (
              <Button type="button" onClick={handleSubmit(onSubmit)}>
                {t("save")}
              </Button>
            )}
            <Button
              onClick={() => {
                setIsAddVehicleFormVisible(false);
              }}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-purple-primary/10"
            >
              {t("cancel")}
            </Button>
          </div>
        </div>
      </form>

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType={contentType}
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default memo(VehicleForm);
